/**
 * Indicator.jsx
 * Individual indicator component for displaying climate data
 * Migration from front/src/components/Indicator.js to React 19 + Modern architecture
 */
import React, { memo } from 'react';
import { Box, Divider } from '@mui/material';
import { useSelector } from 'react-redux';
import { selectOutlook } from '../store/slices/outlookSlice';
import {
  getIndicatorTitle,
  getIndicatorDescription,
  getIndicatorConfiguration,
} from '../config/settings';
import IndicatorTitle from './IndicatorTitle';
import IndicatorDescription from './IndicatorDescription';
import IndicatorTopo from './IndicatorTopo';
import Risks from './Risks';
import IndicatorChart from './IndicatorChart';
import { getIndicatorData, getIndicatorAltiText, getSelectedSeason } from '../utils/dataHelpers';
import { getSeasons } from '../utils/chartsHelpers';

/**
 * Individual indicator component
 * @param {Object} props - Component props
 * @param {Object} props.entity - Entity data (commune/EPCI)
 * @param {string} props.indicatorKey - Unique indicator identifier
 * @param {boolean} props.display - Whether to display the indicator
 * @param {string} props.screenType - Screen type for responsive display
 * @returns {React.ReactElement|null} Indicator component
 */
const Indicator = ({
  entity = {},
  indicatorKey,
  display,
  screenType,
}) => {
  const outlookIndex = useSelector(selectOutlook).index;

  // Get title, description and configuration from settings using helper functions
  const title = getIndicatorTitle(indicatorKey);
  const description = getIndicatorDescription(indicatorKey, entity.domaine);
  const configuration = getIndicatorConfiguration(indicatorKey);

  // Extract indicator data for current outlook
  const indicateurs = entity.indicateurs || [];
  const data = getIndicatorData(indicateurs, indicatorKey)?.[outlookIndex];
  const seasons = data ? getSeasons(data) : [];
  const selectedSeason = getSelectedSeason(seasons, screenType === 'smallScreen');

  // Extract altitude text, with fallback for EPCI T2/T3
  let altiText = getIndicatorAltiText(indicateurs, indicatorKey) || '';
  if (!altiText && (indicatorKey === 'T2' || indicatorKey === 'T3') && entity.massif_rattachement) {
    const alt = indicatorKey === 'T2' ? entity.tranche_alt_min : entity.tranche_alt_max;
    if (alt !== undefined) {
      altiText = `Données pour une altitude proche de ${alt} m - ${entity.massif_rattachement}`;
    }
  }

  // Don't render if not supposed to display
  if (!display) {
    return null;
  }

  // Don't render if no data available for this indicator
  if (!data) {
    return null;
  }

  return (
    <Box
      key={indicatorKey}
      sx={{
        pageBreakInside: 'avoid',
        mb: 2,
      }}
    >
      <Divider sx={{ mb: 2, mt: 4, '@media print': { mb: 0.5, mt: 0 } }} />

      <IndicatorTitle id={indicatorKey}>{title}</IndicatorTitle>

      {/* Charts component - Phase 4.3 ✅ */}
      {/* Hide chart if configuration.hideChart is true (e.g., R8 Cyclone) */}
      {data && !configuration?.hideChart && (
        <IndicatorChart
          entityData={data}
          indicatorKey={indicatorKey}
          seasons={seasons}
          selectedSeason={selectedSeason}
          title={title}
          screenType={screenType}
          domain={entity.domaine}
          altiText={altiText}
        />
      )}

      {/* Topographic information */}
      <IndicatorTopo
        entity={entity}
        indicatorKey={indicatorKey}
      />

      {/* Indicator description */}
      <IndicatorDescription
        description={description}
        type={entity.type}
      />

      {/* Risk warnings */}
      <Risks
        entity={entity}
        indicatorKey={indicatorKey}
      />
    </Box>
  );
};

Indicator.displayName = 'Indicator';

export default memo(Indicator);
